import { useCallback, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useDeployContext } from "@/contexts/DeployContext";
import { useFlow } from "@/contexts/FlowContext";
import { useFlowState } from "@/hooks/useFlowState";
import { useComputedValues } from "@/contexts/ComputedValuesContext";
import { useAnalytics } from "@/contexts/AnalyticsContext";
import { useAsyncBoundOperation } from "@/hooks/useApi";
import { useCurrentWorkflow } from "@/hooks/useCurrentWorkflow";
import { useCurrentWorkspace } from "@/hooks/useCurrentWorkspace";
import { Flows, DeploySteps } from "@/types/flows";
import { AnalyticsFlowEvent } from "@/analytics/types";

export interface UseSignInAndCreateWorkflowOptions {
    /** The step to navigate to after workflow creation. Defaults to DeploySteps.DEPLOY */
    targetStep?: DeploySteps;
    /** Whether to run the workflow setup synchronously. Defaults to false */
    isSync?: boolean;
}

/**
 * Custom hook that handles user sign-in and workflow creation for Rapid.
 * Returns a function that triggers the login flow and creates a workflow upon successful authentication.
 */
export function useSignInAndCreateWorkflow(options: UseSignInAndCreateWorkflowOptions = {}) {
    const { targetStep = DeploySteps.DEPLOY, isSync = false } = options;

    const { task } = useFlow();
    const { setStep, flow } = useFlowState();
    const { user, requireLogin, activeSession } = useAuth();
    const { createRapidWorkflowDeploy, creatingRapidWorkflow } = useDeployContext();
    const { currentInferenceSampledData } = useComputedValues();
    const { track } = useAnalytics();

    const { currentWorkflow } = useCurrentWorkflow();
    const { currentWorkspace } = useCurrentWorkspace();

    const createRapidWorkflow = useCallback(async () => {
        if (user && activeSession && currentWorkspace?.id && !currentWorkflow) {
            try {
                const workflow = await createRapidWorkflowDeploy({
                    task,
                    workspaceId: currentWorkspace.id,
                    groundTruthData: currentInferenceSampledData,
                    isSync
                });
                setStep({
                    flow: Flows.DEPLOY,
                    step: targetStep,
                    workspaceUrl: currentWorkspace.url,
                    workflowUrl: workflow.url
                });
            } catch (err) {
                console.error("useSignInAndCreateWorkflow error", err);
                throw new Error(
                    `There was an error creating your workflow. Please try again. ${err}`
                );
            }
        }
    }, [
        activeSession,
        createRapidWorkflowDeploy,
        currentInferenceSampledData,
        currentWorkspace,
        currentWorkflow,
        isSync,
        setStep,
        targetStep,
        task,
        user
    ]);

    // Prevents auto-invoking when currentWorkflow temporarily becomes undefined during navigation
    const autoInvokeValue = useMemo(
        () => !currentWorkflow && flow === Flows.DEPLOY,
        [currentWorkflow, flow]
    );
    const {
        isLoading: isLoadingCreateRapidWorkflow,
        error: errorCreateRapidWorkflow,
        invoke: invokeCreateRapidWorkflow
    } = useAsyncBoundOperation(createRapidWorkflow, {
        autoInvoke: autoInvokeValue // Only auto-invoke if no workflow exists yet
    });

    const signInAndCreateWorkflow = useCallback(() => {
        track({ event: AnalyticsFlowEvent.GET_API_KEY_CLICKED, data: { task } });
        requireLogin(invokeCreateRapidWorkflow);
    }, [invokeCreateRapidWorkflow, requireLogin, task, track]);

    const loading = creatingRapidWorkflow || isLoadingCreateRapidWorkflow;

    return { signInAndCreateWorkflow, loading, error: errorCreateRapidWorkflow };
}
